import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { defaultButtonClasses } from "@/constants/ui";
import { useVersionCheck } from "@/hooks/useClientVersionQuery";
import { ArrowRight, HardDriveUpload, X } from "lucide-react";

const DeploymentNotification = () => {
    const { isNewVersionAvailable, serverVersion } = useVersionCheck();
    const [isDismissed, setIsDismissed] = useState(false);
    const [lastDismissedVersion, setLastDismissedVersion] = useState<string | null>(null);

    // Reset dismissed state if a newer version is deployed
    useEffect(() => {
        if (serverVersion && serverVersion !== lastDismissedVersion) {
            setIsDismissed(false);
        }
    }, [serverVersion, lastDismissedVersion]);

    const handleDismiss = useCallback(() => {
        setIsDismissed(true);
        setLastDismissedVersion(serverVersion || null);
    }, [serverVersion]);

    const handleUpdate = useCallback(() => {
        // Hard refresh to bypass cache
        window.location.reload();
    }, []);

    // Don't render if no new version or dismissed
    if (!isNewVersionAvailable || isDismissed) {
        return null;
    }

    return (
        <div
            className="bg-blue-400 p-2 pt-3 text-blue-900 flex justify-between items-center"
            role="alert"
            aria-live="polite"
        >
            <p className="p-2 pt-1 flex-1">
                We have updated our system, to ensure continued smooth operation, please update here
                <ArrowRight className="inline-block ml-2 mb-1" size={16} />
            </p>
            <div className="flex gap-2">
                <Button variant="default" className={defaultButtonClasses} onClick={handleUpdate}>
                    <HardDriveUpload size={16} />
                    Update
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    className="text-blue-900 hover:bg-blue-500"
                    onClick={handleDismiss}
                    aria-label="Dismiss notification"
                >
                    <X size={16} />
                </Button>
            </div>
        </div>
    );
};

export default DeploymentNotification;